import { useContext, useEffect, useState } from "react";
import { isAxiosError } from "axios";
import { saveAs } from "file-saver";
import Base from "components/Base";
import { Label } from "components/input/Label";
import AuthContext from "contexts/AuthContext";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { TabPanel, TabView } from "primereact/tabview";
import { Message } from "primereact/message";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import * as Fiscal from "model/fiscal/CicloFiscal";
import CicloFiscalService from "service/fiscal/CicloFiscalService";
const service = new CicloFiscalService();

const ambientes = ["HOMOLOGACAO", "PRODUCAO"].map(ambiente => ({
    label: service.nomeAmbiente(ambiente),
    value: ambiente
}));

const data = (valor: string | null) => valor ? new Date(valor).toLocaleString("pt-BR") : "—";
const mensagem = (e: unknown) => e instanceof Error && !isAxiosError(e) ? e.message : isAxiosError<{ detail?: string; message?: string }>(e) ? e.response?.data?.detail || e.response?.data?.message || "Não foi possível concluir a operação." : "Não foi possível concluir a operação.";
export default function CicloFiscal() {

    const { acoes } = useContext(AuthContext);
    const possui = (sigla: string) => acoes.some(a => a.sigla === sigla);
    const consultar = possui("FISCAL_OPERACAO_CONSULTAR");
    const inutilizar = possui("NOTA_FISCAL_SAIDA_INUTILIZAR");
    const contingenciar = possui("FISCAL_CONTINGENCIA_GERENCIAR");
    const gerenciarEmissao = possui("FISCAL_EMISSAO_DIRETA_GERENCIAR");

    const [unidades, setUnidades] = useState<Fiscal.UnidadeCicloFiscal[]>([]), [unidade, setUnidade] = useState<number | null>(null);
    const [series, setSeries] = useState<Fiscal.SerieCicloFiscal[]>([]);
    const [serie, setSerie] = useState<number | null>(null);

    const [resumo, setResumo] = useState<Fiscal.ResumoFiscal[]>([]);
    const [operacoes, setOperacoes] = useState<Fiscal.OperacaoFiscal[]>([]);
    const [inutilizacoes, setInutilizacoes] = useState<Fiscal.InutilizacaoFiscal[]>([]);
    const [lacunas, setLacunas] = useState<Fiscal.FaixaFiscal[]>([]);
    const [contingencias, setContingencias] = useState<Fiscal.ContingenciaFiscal[]>([]);
    const [requisitos, setRequisitos] = useState<Fiscal.RequisitoFiscal[]>([]);

    const [ambiente, setAmbiente] = useState("HOMOLOGACAO");
    const [justificativa, setJustificativa] = useState("");
    const [ano, setAno] = useState(new Date().getFullYear());
    const [inicio, setInicio] = useState<number | null>(null);
    const [fim, setFim] = useState<number | null>(null);

    const [ocupado, setOcupado] = useState(false);
    const [erro, setErro] = useState("");
    const [sucesso, setSucesso] = useState("");
    const [revisao, setRevisao] = useState(0);

    useEffect(() => {
        let ativo = true;
        const carregar = async () => {
            try {
                const [u, s, r, o, i, c] = await Promise.all([
                    service.unidades(),
                    consultar || inutilizar || gerenciarEmissao ? service.series() : Promise.resolve([]),
                    consultar ? service.resumo() : Promise.resolve([]),
                    consultar ? service.operacoes() : Promise.resolve([]),
                    consultar || inutilizar ? service.inutilizacoes() : Promise.resolve([]),
                    consultar || contingenciar ? service.contingencias() : Promise.resolve([])
                ]);
                if (ativo) {
                    setUnidades(u);
                    setSeries(s);
                    setResumo(r);
                    setOperacoes(o);
                    setInutilizacoes(i);
                    setContingencias(c);
                }
            } catch (e) {
                if (ativo) setErro(mensagem(e));
            }
        };

        if (consultar || inutilizar || contingenciar || gerenciarEmissao) {
            void carregar();
        }
        const timer = consultar ? window.setInterval(() => void carregar(), 15000) : undefined;
        return () => {
            ativo = false;
            if (timer) window.clearInterval(timer);
        };
    }, [consultar, inutilizar, contingenciar, gerenciarEmissao, revisao]);

    useEffect(() => {
        setRequisitos([]);
        if (!unidade || !gerenciarEmissao) return;
        let ativo = true;
        service.requisitos(unidade).then(r => {
            if (ativo) setRequisitos(r);
        }).catch(e => {
            if (ativo) setErro(mensagem(e));
        });
        return () => {
            ativo = false;
        };
    }, [unidade, gerenciarEmissao, revisao]);

    useEffect(() => {
        setSerie(null);
        setLacunas([]);
    }, [unidade]);


    async function executar(acao: () => Promise<unknown>, texto: string) {
        if (ocupado) return;
        setOcupado(true);
        setErro("");
        setSucesso("");
        try {
            await acao();
            setSucesso(texto);
            setRevisao(r => r + 1);
        } catch (e) {
            setErro(mensagem(e));
        } finally {
            setOcupado(false);
        }
    }

    function confirmar(texto: string, acao: () => Promise<unknown>, sucesso: string) {
        confirmDialog({
            header: "Confirmar operação fiscal",
            message: texto,
            acceptLabel: "Confirmar", rejectLabel: "Voltar",
            accept: () => void executar(acao, sucesso)
        });
    }

    const serieAtual = series.find(s => s.id === serie), unidadeAtual = unidades.find(u => u.id === unidade);

    const erroFaixaInutilizacao = inicio !== null && fim !== null
        ? fim < inicio
            ? "O número final deve ser maior ou igual ao número inicial."
            : serieAtual && fim > serieAtual.ultimoNumeroUtilizado
                ? `A faixa não pode ultrapassar o último número utilizado da série (${serieAtual.ultimoNumeroUtilizado}).`
                : ""
        : "";
    if (!consultar && !inutilizar && !contingenciar && !gerenciarEmissao) return <Base tituloPagina="Operações fiscais"><Message severity="warn" text="Sem permissão para acessar operações fiscais." /></Base>;
    return <Base tituloPagina="Operações fiscais">
        {erro && <Message severity="error" text={erro} className="mb-3" />}{sucesso && <Message severity="success" text={sucesso} className="mb-3" />}
        <div className="d-flex gap-3 mb-3 flex-wrap">
            <Dropdown 
                value={unidade} 
                options={unidades} 
                optionLabel="nome" optionValue="id" 
                style={{ minWidth: "200px" }}
                placeholder="Unidade organizacional" onChange={e => setUnidade(e.value)} disabled={ocupado} />
            <Dropdown 
                value={ambiente} 
                options={ambientes} 
                style={{ minWidth: "200px" }}
                optionLabel="label" optionValue="value" onChange={e => setAmbiente(e.value)} disabled={ocupado} />
            <Button label="Atualizar" icon="pi pi-refresh" outlined onClick={() => setRevisao(r => r + 1)} disabled={ocupado} />
            <Button type="button" label="Documentação" icon="pi pi-book" outlined
                onClick={() => window.open('https://empxdocs.appsotecnologia.com.br/fiscal/operacoes-fiscais', '_blank', 'noopener,noreferrer')} />
        </div>
        <TabView>
            {consultar && <TabPanel header="Monitoramento"><p>As operações e os efeitos internos possuem situações independentes. Resultados indeterminados exigem conciliação.</p>
                <DataTable value={resumo} emptyMessage="Nenhuma pendência"><Column field="tipo" header="Tipo" body={(registro: Fiscal.ResumoFiscal) => service.nomeTipo(registro.tipo)} /><Column field="situacao" header="Situação" body={(registro: Fiscal.ResumoFiscal) => service.nomeSituacao(registro.situacao)} /><Column field="quantidade" header="Quantidade" /><Column header="Mais antiga" body={(r: Fiscal.ResumoFiscal) => data(r.mais_antiga)} /></DataTable>
                <h3>Últimas 200 operações</h3><DataTable value={operacoes} paginator rows={20} emptyMessage="Nenhuma operação"><Column field="id" header="Operação" /><Column field="nota" header="Nota" /><Column field="inutilizacao" header="Inutilização" /><Column field="tipo" header="Tipo" body={(operacao: Fiscal.OperacaoFiscal) => service.nomeTipo(operacao.tipo)} /><Column field="ambiente" header="Ambiente" body={(operacao: Fiscal.OperacaoFiscal) => service.nomeAmbiente(operacao.ambiente)} /><Column field="situacao" header="Situação" body={(operacao: Fiscal.OperacaoFiscal) => service.nomeSituacao(operacao.situacao)} /><Column field="tentativas" header="Tentativas" /><Column field="cstat" header="cStat" /><Column field="motivo" header="Retorno" /><Column body={(o: Fiscal.OperacaoFiscal) => possui("NOTA_FISCAL_SAIDA_RECONCILIAR") && ["MANUAL", "INCERTA"].includes(o.situacao) ? <Button label="Reconciliar" disabled={ocupado} onClick={() => void executar(() => service.reconciliar(o.id), "Conciliação agendada; acompanhe o retorno.")} /> : null} /></DataTable>
            </TabPanel>}
            {(consultar || inutilizar) && <TabPanel header="Numeração e inutilização">
                <p>Selecione uma série. Números autorizados, cancelados, denegados ou com envio indeterminado não podem ser inutilizados.</p>
                <div className="mb-3">
                    <Label htmlFor="serie-inutilizacao" className="d-block mb-2" obrigatorio>Série</Label>
                    <Dropdown
                        inputId="serie-inutilizacao"
                        value={serie}
                        options={series.filter(s => s.unidade === unidade && s.ambiente === ambiente)
                            .map(s => ({ ...s, label: `Série ${s.serie} · último número ${s.ultimoNumeroUtilizado}` }))}
                        optionLabel="label"
                        optionValue="id"
                        placeholder="Selecione uma série"
                        onChange={e => { setSerie(e.value); setLacunas([]); }}
                        disabled={ocupado}
                        className="w-100"
                    />
                    <div className="mt-3">
                        <Button
                            label="Consultar lacunas"
                            outlined
                            disabled={!serie || ocupado}
                            onClick={() => void executar(async () => setLacunas(await service.lacunas(serie!)), "Lacunas consultadas.")}
                        />
                    </div>
                </div>
                <DataTable value={lacunas} emptyMessage="Consulte as lacunas da série">
                    <Column field="inicio" header="Inicial" />
                    <Column field="fim" header="Final" />
                    <Column body={(f: Fiscal.FaixaFiscal) => inutilizar ? <Button label="Selecionar" outlined onClick={() => { setInicio(f.inicio); setFim(f.fim); }} /> : null} />
                </DataTable>

                {inutilizar && (
                    <div className="mt-3 mb-3">
                        <div className="row">
                            <div className="col-12 col-md-4 mb-3">
                                <Label htmlFor="ano-inutilizacao" className="d-block mb-2" obrigatorio>Ano</Label>
                                <InputNumber
                                    inputId="ano-inutilizacao"
                                    value={ano}
                                    useGrouping={false}
                                    min={2006}
                                    max={new Date().getFullYear()}
                                    onValueChange={e => setAno(e.value ?? new Date().getFullYear())}
                                    className="w-100"
                                    inputClassName="w-100"
                                />
                            </div>
                            <div className="col-12 col-md-4 mb-3">
                                <Label htmlFor="numero-inicial-inutilizacao" className="d-block mb-2" obrigatorio>Número inicial</Label>
                                <InputNumber
                                    inputId="numero-inicial-inutilizacao"
                                    value={inicio}
                                    useGrouping={false}
                                    min={1}
                                    max={999999999}
                                    onValueChange={e => setInicio(e.value ?? null)}
                                    className="w-100"
                                    inputClassName="w-100"
                                />
                            </div>
                            <div className="col-12 col-md-4 mb-3">
                                <Label htmlFor="numero-final-inutilizacao" className="d-block mb-2" obrigatorio>Número final</Label>
                                <InputNumber
                                    inputId="numero-final-inutilizacao"
                                    value={fim}
                                    useGrouping={false}
                                    min={1}
                                    max={999999999}
                                    onValueChange={e => setFim(e.value ?? null)}
                                    className="w-100"
                                    inputClassName="w-100"
                                />
                            </div>
                        </div>
                        {erroFaixaInutilizacao && (
                            <small className="text-danger d-block mb-3" role="alert">{erroFaixaInutilizacao}</small>
                        )}
                        <Label htmlFor="justificativa-inutilizacao" className="d-block mb-2" obrigatorio>Justificativa</Label>
                        <InputTextarea
                            id="justificativa-inutilizacao"
                            value={justificativa}
                            onChange={e => setJustificativa(e.target.value)}
                            rows={3}
                            maxLength={255}
                            className="w-100"
                        />
                        <div className="mt-3">
                            <Button
                                label="Solicitar inutilização"
                                disabled={ocupado || !serieAtual || !inicio || !fim || !!erroFaixaInutilizacao || justificativa.trim().length < 15}
                                onClick={() => confirmar(
                                    `Inutilizar a faixa ${inicio}–${fim} em ${service.nomeAmbiente(ambiente)}?`,
                                    () => service.inutilizar({
                                        idSerieNotaFiscal: serie!,
                                        ano,
                                        numeroInicial: inicio!,
                                        numeroFinal: fim!,
                                        justificativa: justificativa.trim(),
                                        idRequisicao: crypto.randomUUID()
                                    }),
                                    "Inutilização agendada. A faixa só estará inutilizada após confirmação da SEFAZ."
                                )}
                            />
                        </div>
                    </div>
                )}
                <h3>Solicitações</h3><DataTable value={inutilizacoes} paginator rows={15} emptyMessage="Nenhuma solicitação"><Column field="serie" header="Série" /><Column field="ambiente" header="Ambiente" body={(registro: Fiscal.InutilizacaoFiscal) => service.nomeAmbiente(registro.ambiente)} /><Column field="ano" header="Ano" /><Column field="numeroInicial" header="Inicial" /><Column field="numeroFinal" header="Final" /><Column field="status" header="Situação" body={(registro: Fiscal.InutilizacaoFiscal) => service.nomeSituacao(registro.status)} /><Column field="protocolo" header="Protocolo" /><Column field="motivoSefaz" header="Retorno" /><Column body={(i: Fiscal.InutilizacaoFiscal) => i.status === "INUTILIZADA" ? <Button label="XML" icon="pi pi-download" onClick={() => void executar(async () => saveAs(await service.xmlInutilizacao(i.id), `inutilizacao-${i.id}.xml`), "XML obtido.")} /> : null} /></DataTable>
            </TabPanel>}
            {(consultar || contingenciar) && <TabPanel header="Contingência">
                <p>A ativação verifica a disponibilidade da SVC-AN. Novas notas usam uma chave de contingência; notas já numeradas conservam sua modalidade original.</p>
                {contingenciar && (
                    <div className="mb-3">
                        <Label htmlFor="justificativa-contingencia" className="d-block mb-2" obrigatorio>
                            Justificativa
                        </Label>
                        <InputTextarea
                            id="justificativa-contingencia"
                            value={justificativa}
                            onChange={e => setJustificativa(e.target.value)}
                            rows={3}
                            maxLength={255}
                            className="w-100"
                        />
                        <div className="mt-3">
                            <Button
                                label="Verificar e ativar SVC-AN"
                                disabled={!unidade || ocupado || justificativa.trim().length < 15}
                                onClick={() => confirmar(
                                    `Ativar contingência em ${service.nomeAmbiente(ambiente)} para esta unidade?`,
                                    () => service.ativarContingencia({
                                        idUnidadeOrganizacional: unidade!,
                                        ambiente,
                                        justificativa: justificativa.trim()
                                    }),
                                    "Contingência ativada após confirmação de disponibilidade."
                                )}
                            />
                        </div>
                    </div>
                )}
                <DataTable value={contingencias.filter(c => !unidade || c.idUnidadeOrganizacional === unidade)} emptyMessage="Nenhuma contingência"><Column field="ambiente" header="Ambiente" body={(registro: Fiscal.ContingenciaFiscal) => service.nomeAmbiente(registro.ambiente)} /><Column field="modalidade" header="Modalidade" /><Column field="status" header="Situação" body={(registro: Fiscal.ContingenciaFiscal) => service.nomeSituacao(registro.status)} /><Column header="Início" body={(c: Fiscal.ContingenciaFiscal) => data(c.inicio)} /><Column field="justificativa" header="Justificativa" /><Column body={(c: Fiscal.ContingenciaFiscal) => contingenciar && c.status === "ATIVA" ? <Button label="Encerrar" disabled={ocupado} onClick={() => confirmar("Encerrar a contingência e usar SVRS nas novas notas?", () => service.encerrarContingencia(c.id), "Contingência encerrada.")} /> : null} /></DataTable>
            </TabPanel>}
            {gerenciarEmissao && <TabPanel header="Ativação da emissão">
                <h3>Liberação da unidade</h3>
                <p>Situação: {unidadeAtual?.situacaoEmissaoDireta ? service.nomeSituacao(unidadeAtual.situacaoEmissaoDireta) : "Produção não liberada"}{unidadeAtual?.pausarNovosEnvios ? " · novas emissões pausadas" : ""}</p>
                <DataTable value={requisitos} emptyMessage="Selecione a unidade">
                    <Column field="descricao" header="Requisito" />
                    <Column header="Resultado" body={(r: Fiscal.RequisitoFiscal) => r.atendido ? "Atendido" : "Pendente"} />
                </DataTable>
                <div className="d-flex gap-3 mt-3 flex-wrap">
                    <Button
                        label="Ativar produção"
                        disabled={!unidade || ocupado || requisitos.length === 0 || requisitos.some(r => !r.atendido)}
                        onClick={() => confirmar("Liberar a emissão direta em produção para esta unidade?", () => service.ativar(unidade!), "Emissão direta liberada para a unidade.")} />
                    <Button
                        label={unidadeAtual?.pausarNovosEnvios ? "Retomar novas emissões" : "Pausar novas emissões"}
                        outlined disabled={!unidade || ocupado}
                        onClick={() => confirmar("Alterar a pausa de novas emissões? Consultas e recuperação continuarão disponíveis.", () => service.pausar(unidade!, !unidadeAtual?.pausarNovosEnvios), "Pausa atualizada.")} />
                </div>
            </TabPanel>}
        </TabView>
    </Base>;
}
